import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import dayjs from 'dayjs'

// Export renders on white (DESIGN.md §4.12)
const EXPORT_BACKGROUND = '#FFFFFF'
const EXPORT_BORDER = '#DFDBD8'
// Render each page at 2× CSS pixels (~330 dpi on A4 for a 1280px-wide page)
const RENDER_SCALE = 2
const PAGE_MARGIN_MM = 10
// A page break may not leave the page emptier than this (otherwise fall back to finer cuts)
const MIN_PAGE_FILL = 0.35

/**
 * Print-only overrides applied to the html2canvas clone:
 * sticky elements become static, inset shadows (badly drawn by html2canvas) become plain borders,
 * truncated text is not clipped, and negative letter-spacing (drawn glyph by glyph, which garbles
 * text) is reset.
 */
const PRINT_CSS = `
[data-print-static] { position: static !important; top: auto !important; }
[data-print-hide] { display: none !important; }
* { letter-spacing: normal !important; }
.card, .btn-secondary, .shadow-rim, .shadow-control { box-shadow: none !important; border: 1px solid ${EXPORT_BORDER} !important; }
[role='group'] { box-shadow: none !important; }
[role='group'] > button { display: inline-flex !important; align-items: center !important; justify-content: center !important; line-height: 1.25rem !important; box-shadow: none !important; }
[role='group'] > button[aria-pressed='true'] { border: 1px solid ${EXPORT_BORDER} !important; }
.truncate { overflow: visible !important; text-overflow: clip !important; line-height: 1.4 !important; }
.animate-pulse, .animate-spin { animation: none !important; }
`

// Elements a page break must not cut through. Coarse first (whole cards), then finer.
const KEEP_TOGETHER_COARSE = 'section, .card, header, footer'
const KEEP_TOGETHER_FINE = 'svg, img, canvas, tr, li, p, h1, h2, h3, h4, button, input, .rounded-xl, .pill'

interface IInterval { top: number, bottom: number }

const waitForFrames = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

/** Replace native date inputs (which html2canvas draws as raw ISO values) with DD/MM/YYYY text. */
const replaceDateInputs = (doc: Document) => {
    doc.querySelectorAll<HTMLInputElement>('input[type="date"]').forEach((input) => {
        const box = doc.createElement('div')
        box.className = input.className
        box.style.display = 'flex'
        box.style.alignItems = 'center'
        box.textContent = input.value ? dayjs(input.value).format('DD/MM/YYYY') : '—'
        input.replaceWith(box)
    })
}

/** Vertical extents (relative to root) of visible elements matching `selector`. */
const getIntervals = (root: HTMLElement, selector: string): IInterval[] => {
    const rootTop = root.getBoundingClientRect().top
    return Array.from(root.querySelectorAll<HTMLElement>(selector))
        .filter((el) => !el.closest('[data-print-hide]'))
        .map((el) => el.getBoundingClientRect())
        .filter((rect) => rect.height > 0 && rect.width > 0)
        .map((rect) => ({ top: rect.top - rootTop, bottom: rect.bottom - rootTop }))
}

/** Lowest y in (min, max] that doesn't cut through any interval, preferring element edges. */
const findSafeCut = (intervals: IInterval[], min: number, max: number) => {
    const candidates = intervals
        .flatMap(({ top, bottom }) => [top, bottom])
        .filter((y) => y > min && y <= max)
        .sort((a, b) => b - a)
    return candidates.find((y) => intervals.every(({ top, bottom }) => y <= top + 0.5 || y >= bottom - 0.5))
}

/** Split the root's height into page slices whose boundaries avoid cutting cards, charts and text. */
const getPageSlices = (root: HTMLElement, pageHeightPx: number) => {
    const total = root.scrollHeight
    const coarse = getIntervals(root, KEEP_TOGETHER_COARSE)
    const fine = getIntervals(root, KEEP_TOGETHER_FINE)
    const slices: IInterval[] = []
    let start = 0
    while (start < total - 1) {
        const max = start + pageHeightPx
        if (max >= total) {
            slices.push({ top: start, bottom: total })
            break
        }
        const min = start + pageHeightPx * MIN_PAGE_FILL
        const end = findSafeCut([...coarse, ...fine], min, max)
            ?? findSafeCut(fine, min, max)
            ?? max
        slices.push({ top: start, bottom: end })
        start = end
    }
    return slices
}

/** Export `root` to a multi-page A4 PDF with crisp per-page renders and clean page breaks. */
export const exportElementToPdf = async (root: HTMLElement, fileName: string) => {
    const { scrollX, scrollY } = window
    // Apply the print overrides to the live page (not just the clone) so the page breaks
    // measured here match the rendered layout exactly
    const printStyle = document.createElement('style')
    printStyle.textContent = PRINT_CSS
    document.head.appendChild(printStyle)
    try {
        // html2canvas renders relative to the viewport; lay out from the top so sticky
        // elements sit in their natural position
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
        await document.fonts?.ready
        await waitForFrames()

        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
        const contentWidthMm = pdf.internal.pageSize.getWidth() - PAGE_MARGIN_MM * 2
        const contentHeightMm = pdf.internal.pageSize.getHeight() - PAGE_MARGIN_MM * 2
        const width = root.scrollWidth
        const mmPerPx = contentWidthMm / width
        const slices = getPageSlices(root, contentHeightMm / mmPerPx)

        for (let index = 0; index < slices.length; index++) {
            const slice = slices[index]
            const canvas = await html2canvas(root, {
                backgroundColor: EXPORT_BACKGROUND,
                useCORS: true,
                scale: RENDER_SCALE,
                x: 0,
                y: slice.top,
                width,
                height: Math.ceil(slice.bottom - slice.top),
                scrollX: 0,
                scrollY: 0,
                windowWidth: width,
                onclone: (doc) => {
                    const clonedRoot = doc.getElementById(root.id)
                    if (clonedRoot) clonedRoot.style.backgroundColor = EXPORT_BACKGROUND
                    replaceDateInputs(doc)
                },
            })
            if (index > 0) pdf.addPage()
            pdf.addImage(canvas, 'PNG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, contentWidthMm, canvas.height / RENDER_SCALE * mmPerPx, undefined, 'FAST')
        }
        pdf.save(fileName)
    } finally {
        printStyle.remove()
        window.scrollTo({ top: scrollY, left: scrollX, behavior: 'instant' as ScrollBehavior })
    }
}
